import React, { memo, useMemo, useCallback } from "react";
import { Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import SkillIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/skilles.svg";
import ExperienceIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Experience.svg";
import ExpertiseIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Expertise.svg";
import ActionIcon from "../../../../assets/EmployeeProfileIcons/Profile_Page_Icons/Action.svg";

const EmpSkillTableComponent = memo(({
  data = [],
  canEdit = false,
  onEdit,
  noDataMessage = "No Update Skill Available",
}) => {
  const columns = useMemo(() => [
    { 
      key: "Skill", 
      label: "Skill", 
      icon: SkillIcon,
      width: "15%"
    },
    { 
      key: "SubSkill", 
      label: "SubSkill", 
      icon: SkillIcon,
      width: "15%"
    },
    { 
      key: "Experience", 
      label: "Experience", 
      icon: ExperienceIcon, 
      center: true,
      width: "10%"
    },
    { 
      key: "SkillDetails", 
      label: "Skill Details", 
      icon: SkillIcon,
      width: "25%"
    },
    { 
      key: "Ratings", 
      label: "Expertise Out of 5", 
      icon: ExpertiseIcon, 
      center: true,
      width: "10%"
    },
    { 
      key: "SkillType", 
      label: "Skill Type", 
      icon: SkillIcon,
      width: "15%",
      format: (value) => {
        return value === 1 ? "Primary" : value === 2 ? "Secondary" : "";
      }
    },
    { 
      key: "action", 
      label: "Action", 
      icon: ActionIcon, 
      isAction: true,
      width: "10%"
    },
  ], []);

  const renderTooltip = useCallback((text) => <Tooltip>{text}</Tooltip>, []);

  const renderCellContent = useCallback((text, center = false) => {
    const displayText = text || "";
    return (
      <OverlayTrigger
        placement="top"
        overlay={renderTooltip(displayText)}
      >
        <span 
          className={`d-inline-block text-truncate ${center ? 'mx-auto' : ''}`} 
          style={{ 
            maxWidth: "100%",
            display: 'inline-block'
          }}
        >
          {displayText}
        </span>
      </OverlayTrigger>
    );
  }, [renderTooltip]);

  const handleEditClick = useCallback((item) => {
    if (canEdit && onEdit) {
      onEdit(item);
    }
  }, [canEdit, onEdit]);

  return (
    <div className="skills-table-container">
      <style>
        {`
          .skills-table-container {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .fixed-skills-table {
            table-layout: fixed;
            width: 100%;
            min-width: 900px; /* Minimum width to ensure columns don't collapse */
          }
          
          .fixed-width-column {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          @media (min-width: 992px) {
            .fixed-skills-table {
              min-width: 100%;
            }
          }
          
          .tech-icon {
            width: 20px;
            height: 20px;
          }
        `}
      </style>
      
      <Table striped bordered hover className="fixed-skills-table">
        <colgroup>
          {columns.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead className="text-center">
          <tr>
            {columns.map((column) => (
              <th 
                key={column.key}
                style={{ width: column.width }}
              >
                <div className="d-flex flex-column align-items-center fixed-width-column">
                  <div>
                    <img
                      src={column.icon}
                      alt={column.label}
                      className="tech-icon"
                    />
                  </div>
                  <div style={{ fontSize: "12px" }}>{column.label}</div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => {
                  if (column.isAction) {
                    return (
                      <td 
                        key={column.key} 
                        className="text-center fixed-width-column"
                        style={{ width: column.width }}
                      >
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{canEdit ? "Edit" : "Edit disabled"}</Tooltip>}
                        >
                          <span
                            onClick={() => handleEditClick(item)}
                            style={{
                              cursor: canEdit ? "pointer" : "not-allowed",
                              opacity: canEdit ? 1 : 0.5,
                            }}
                          >
                            <i className="bi bi-pencil-square"></i>
                          </span>
                        </OverlayTrigger>
                      </td>
                    );
                  }
                  
                  const cellValue = column.format 
                    ? column.format(item[column.key])
                    : item[column.key] || "";
                  
                  return (
                    <td 
                      key={column.key} 
                      className={`fixed-width-column ${column.center ? "text-center" : ""}`}
                      style={{ width: column.width }}
                    >
                      {renderCellContent(cellValue, column.center)}
                    </td>
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="text-center">
                {noDataMessage}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
});

export default EmpSkillTableComponent;